/**
 * CampusDirectory — public directory at `/campuses`, dark-navy aesthetic
 * matching Landing. Same Nav / footer chrome, Space Grotesk display, Inter
 * body, blue accents. Search filters live client-side across all groups.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import ScrollProgress from "@/components/ScrollProgress";
import { Input } from "@/components/ui/input";
import {
  GraduationCap, MapPin, ArrowRight, Search, Building2, Loader2, Sparkles,
} from "lucide-react";

// Deterministic blue-shade gradient per campus for the card header (so each
// card looks distinct without needing a real cover image). Six variants across
// blue/indigo/violet keep everything on-palette.
const HEADER_GRADIENTS = [
  "linear-gradient(135deg, #2c4bce 0%, #1a2a7a 100%)",
  "linear-gradient(135deg, #4066e2 0%, #26449e 100%)",
  "linear-gradient(135deg, #6b8dff 0%, #2c4bce 100%)",
  "linear-gradient(135deg, #7c3aed 0%, #1a2a7a 100%)",
  "linear-gradient(135deg, #2c4bce 0%, #7c3aed 100%)",
  "linear-gradient(135deg, #1a2a7a 0%, #0a1a5c 100%)",
];
function pickGradient(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return HEADER_GRADIENTS[hash % HEADER_GRADIENTS.length];
}

// ============================================================================
// Nav
// ============================================================================
function Nav() {
  return (
    <nav className="fixed top-0 inset-x-0 z-40 border-b border-white/10 backdrop-blur-xl bg-[#050510]/70">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-white text-lg tracking-tight">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 grid place-items-center">
            <GraduationCap className="h-4 w-4 text-white" />
          </div>
          EduKampus
        </Link>
        <div className="hidden md:flex items-center gap-6 ml-6 text-sm text-white/70">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <Link to="/#modules" className="hover:text-white transition-colors">Modules</Link>
          <Link to="/#faq" className="hover:text-white transition-colors">FAQ</Link>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/login" className="text-sm text-white/70 hover:text-white px-3 py-2" data-testid="platform-login-link">Sign in</Link>
        </div>
      </div>
    </nav>
  );
}

// ============================================================================
// Campus card
// ============================================================================
function CampusCard({ institution, campus, index }) {
  const gradient = pickGradient(campus.portal_slug);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: (index % 6) * 0.06, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={campus.portal_path}
        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] block hover:border-blue-500/40 hover:bg-white/[0.05] transition-all hover:-translate-y-0.5"
        data-testid={`campus-card-${campus.portal_slug}`}
      >
        {/* Header — image or blue gradient */}
        <div className="relative h-32 overflow-hidden" style={{ background: gradient }}>
          {campus.background_url && (
            <img src={campus.background_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
          )}
          {!campus.background_url && (
            <div
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.35) 0px, transparent 40%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.25) 0px, transparent 40%)",
              }}
            />
          )}
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(5,5,16,0.55) 0%, transparent 60%)" }} />
          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-white">
            {campus.logo_url ? (
              <img
                src={campus.logo_url}
                alt={campus.name}
                className="h-10 w-10 rounded-lg object-contain border p-1"
                style={{ background: "rgba(255,255,255,0.15)", borderColor: "rgba(255,255,255,0.25)", backdropFilter: "blur(6px)" }}
              />
            ) : (
              <div className="h-10 w-10 rounded-lg border flex items-center justify-center"
                   style={{ background: "rgba(255,255,255,0.15)", borderColor: "rgba(255,255,255,0.25)", backdropFilter: "blur(6px)" }}>
                <GraduationCap className="h-5 w-5" />
              </div>
            )}
            {campus.city && (
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 rounded-md flex items-center gap-1 border"
                   style={{ background: "rgba(5,5,16,0.4)", borderColor: "rgba(255,255,255,0.2)", backdropFilter: "blur(6px)" }}>
                <MapPin className="h-3 w-3" />
                {campus.city}
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="p-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-blue-400 mb-1.5">
            {institution.name}
          </div>
          <h3 className="font-display font-bold text-lg leading-tight tracking-tight text-white mb-3">
            {campus.name}
          </h3>
          <div className="flex items-center justify-between pt-3 border-t border-white/10">
            <span className="text-xs font-mono text-white/40 truncate">/p/{campus.portal_slug}</span>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-300 transition-all group-hover:gap-2">
              Enter portal
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

// ============================================================================
// Page
// ============================================================================
export default function CampusDirectory() {
  const [q, setQ] = useState("");

  const dirQ = useQuery({
    queryKey: ["public-directory"],
    queryFn: () => api.get("/public/directory").then((r) => r.data),
    staleTime: 5 * 60_000,
  });

  useEffect(() => { document.title = "Campus Directory — Kampus 365"; }, []);

  const filtered = useMemo(() => {
    const groups = dirQ.data?.groups || [];
    if (!q.trim()) return groups;
    const needle = q.trim().toLowerCase();
    return groups
      .map((g) => ({
        institution: g.institution,
        campuses: g.campuses.filter(
          (c) =>
            c.name.toLowerCase().includes(needle) ||
            (c.city || "").toLowerCase().includes(needle) ||
            c.portal_slug.toLowerCase().includes(needle) ||
            g.institution.name.toLowerCase().includes(needle),
        ),
      }))
      .filter((g) => g.campuses.length > 0);
  }, [q, dirQ.data]);

  const totalCampuses = dirQ.data?.total_campuses || 0;
  const totalInstitutions = dirQ.data?.total_institutions || 0;

  return (
    <div className="min-h-screen bg-[#050510] text-white font-sans" data-testid="campus-directory-page">
      <ScrollProgress color="#3b82f6" />
      <Nav />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-[#050510] text-white pt-32 pb-16">
        {/* Ambient background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-blue-600/25 blur-3xl" />
          <div className="absolute -bottom-20 -right-40 h-[420px] w-[420px] rounded-full bg-indigo-500/20 blur-3xl" />
          <div className="absolute inset-0 grid-pattern opacity-[0.06]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.2em] text-white/80 mb-8">
            <Sparkles className="h-3 w-3 text-blue-400" />
            Campus Directory
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.05 }}
            className="font-display font-extrabold text-[clamp(2.5rem,6vw,5rem)] leading-[0.9] tracking-[-0.03em] max-w-4xl">
            Find your school&apos;s{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-sky-300 to-white">
              portal.
            </span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-white/70 text-lg leading-relaxed max-w-2xl">
            Every campus running on EduKampus. Click any card to sign in to your parent,
            student, or staff portal.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.28 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
            {[
              { v: String(totalCampuses), l: "Live campuses", tid: "stat-total-campuses" },
              { v: String(totalInstitutions), l: "Institutions", tid: "stat-total-institutions" },
              { v: "24/7", l: "Portal access", tid: "stat-uptime" },
            ].map((s) => (
              <div key={s.l} className="border-l-2 border-blue-500/60 pl-4">
                <div className="font-display font-extrabold text-3xl sm:text-4xl" data-testid={s.tid}>{s.v}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ============ STICKY SEARCH BAR ============ */}
      <div className="sticky top-16 z-20 border-y border-white/10 backdrop-blur-xl bg-[#050510]/80">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              placeholder="Search by campus name, city, or institution…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-11 bg-white/[0.03] border-white/10 text-white placeholder:text-white/40"
              data-testid="directory-search"
            />
          </div>
          {q && (
            <button
              onClick={() => setQ("")}
              className="text-xs font-mono uppercase tracking-[0.2em] px-3 py-2 rounded-full text-blue-400 hover:text-blue-300 transition-colors"
              data-testid="directory-clear-search"
            >
              Clear
            </button>
          )}
          <div className="ml-auto text-xs hidden sm:block text-white/50">
            <Link to="/login" className="underline underline-offset-4 hover:text-white">Platform login →</Link>
          </div>
        </div>
      </div>

      {/* ============ RESULTS ============ */}
      <div className="max-w-6xl mx-auto px-6 py-14">
        {dirQ.isLoading && (
          <div className="flex items-center justify-center py-24 text-white/60">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading directory…
          </div>
        )}

        {!dirQ.isLoading && filtered.length === 0 && (
          <div className="text-center py-24" data-testid="directory-empty-state">
            <div className="inline-flex h-14 w-14 rounded-2xl items-center justify-center mb-4 border border-white/10 bg-white/[0.03]">
              <Search className="h-6 w-6 text-white/40" />
            </div>
            <h3 className="font-display font-bold text-xl mb-1 text-white">
              No campuses match &ldquo;{q}&rdquo;
            </h3>
            <p className="text-sm text-white/50">
              Try a different name, city, or clear the search.
            </p>
          </div>
        )}

        {filtered.map((group) => (
          <section key={group.institution.id} className="mb-16" data-testid={`institution-group-${group.institution.slug}`}>
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between mb-6 gap-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-blue-400 mb-1.5">
                  <Building2 className="h-3 w-3 inline -mt-0.5 mr-1.5" />
                  Institution
                </div>
                <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-white">
                  {group.institution.name}
                </h2>
                {group.institution.tagline && (
                  <p className="text-sm mt-1 text-white/50">{group.institution.tagline}</p>
                )}
              </div>
              <div className="text-xs font-mono text-white/40">
                {group.campuses.length} campus{group.campuses.length === 1 ? "" : "es"}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {group.campuses.map((c, i) => (
                <CampusCard key={c.id} institution={group.institution} campus={c} index={i} />
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* ============ FOOTER ============ */}
      <footer className="bg-[#050510] text-white/60 border-t border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <div className="font-mono uppercase tracking-[0.25em] text-white/40">
            © {new Date().getFullYear()} EduKampus · Multi-tenant SaaS
          </div>
          <div className="flex gap-5">
            <Link to="/login" className="hover:text-white">Platform login</Link>
            <a href="mailto:hello@edukampus.com" className="hover:text-white">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
